import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {safeCaptureException} from '../../utils/sentry';
import {WebcamRefTypes} from '@chekinapp/ui';
import {API} from '@chekin/api';
import {throttle, useSyncRef} from '@chekin/core';
import {useFaceDetectionSocket, type ServerDetectionResponse} from '../faceDetection';

const STATE_THROTTLE = 150;
const FRAME_INTERVAL = 150;

type DocumentDetectionData = {
  is_document_detected?: boolean;
  is_capture_ready?: boolean;
  guidance?: string | null;
  area_percent?: number | null;
  is_sharp?: boolean;
  is_well_lit?: boolean;
  is_fully_visible?: boolean;
  confidence?: number | null;
  corners?: unknown;
};

type DocumentDetectionState = {
  isCaptureReady: boolean;
  guidance: string | null;
};

const IDLE_STATE: DocumentDetectionState = {
  isCaptureReady: false,
  guidance: null,
};

const IGNORED_GUIDANCE = ['show your document', 'fit the whole document in frame'];

const resolveGuidance = (guidance?: string | null): string | null => {
  if (!guidance) return null;
  return IGNORED_GUIDANCE.includes(guidance.trim().toLowerCase()) ? null : guidance;
};

const toState = (data: DocumentDetectionData): DocumentDetectionState => ({
  isCaptureReady: Boolean(data.is_capture_ready),
  guidance: resolveGuidance(data.guidance),
});

interface UseServerDocumentDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  shouldInitialize?: boolean;
}

export const useServerDocumentDetection = ({
  videoRef,
  shouldInitialize = true,
}: UseServerDocumentDetectionProps) => {
  const [state, setState] = useState<DocumentDetectionState>(IDLE_STATE);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const throttledSetState = useRef(
    throttle((next: DocumentDetectionState) => {
      setState(next);
    }, STATE_THROTTLE),
  ).current;

  const handleMessage = useCallback(
    (response: ServerDetectionResponse) => {
      if (response.type !== 'detection') return;
      throttledSetState(toState(response.data as DocumentDetectionData));
    },
    [throttledSetState],
  );

  const createSocket = useCallback(
    () => API.webSocket.connectDocumentDetectionWebSocket(),
    [],
  );

  const {isConnected, sendData} = useFaceDetectionSocket({
    onMessage: handleMessage,
    autoConnect: shouldInitialize,
    createSocket,
  });
  const isConnectedRef = useSyncRef(isConnected);
  const shouldInitializeRef = useSyncRef(shouldInitialize);

  useEffect(() => {
    if (!shouldInitialize) {
      return;
    }

    canvasRef.current = document.createElement('canvas');
    return () => {
      canvasRef.current = null;
    };
  }, [shouldInitialize]);

  const sendFrame = useCallback(() => {
    if (
      !shouldInitializeRef.current ||
      !isConnectedRef.current ||
      !canvasRef.current ||
      !videoRef?.current?.video
    ) {
      return;
    }

    const video = videoRef.current.video;
    if (video.readyState !== 4) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      async blob => {
        if (!blob) return;

        try {
          const arrayBuffer = await blob.arrayBuffer();
          sendData(arrayBuffer);
        } catch (err) {
          safeCaptureException(err, 'Preparing a document frame for detection');
          console.error('Error preparing document frame for WebSocket:', err);
        }
      },
      'image/jpeg',
      0.8,
    );
  }, [isConnectedRef, sendData, videoRef, shouldInitializeRef]);

  const startDetection = useCallback(() => {
    if (!shouldInitialize) {
      return;
    }

    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
    }

    frameIntervalRef.current = setInterval(sendFrame, FRAME_INTERVAL);
  }, [sendFrame, shouldInitialize]);

  useEffect(() => {
    return () => {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
    };
  }, []);

  return {
    isModelLoading: shouldInitialize ? !isConnected : false,
    isCaptureReady: state.isCaptureReady,
    guidance: state.guidance,
    startDetection,
  };
};
